const EST_TIMEZONE = 'America/New_York'

// DST-aware US Eastern abbreviation for a given Date: "EST" in winter (UTC-5),
// "EDT" in spring/summer (UTC-4). Derived from the real America/New_York offset
// so labels track the actual season instead of being hardcoded.
export function easternTzAbbr (date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TIMEZONE,
    timeZoneName: 'short'
  }).formatToParts(date)
  const tz = parts.find(p => p.type === 'timeZoneName')
  return tz ? tz.value : 'EST'
}

export function formatDate (dateStr, options = {}) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''

  const formatted = date.toLocaleDateString('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  })
  return formatted
}

export function formatDateShort (dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''

  const formatted = date.toLocaleDateString('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
  return formatted
}

export function formatDateFull (dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''

  const datePart = date.toLocaleDateString('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  const timePart = date.toLocaleTimeString('en-US', {
    timeZone: EST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })

  return `${datePart} at ${timePart} ${easternTzAbbr(date)}`
}

export function formatDateTime (dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''

  const formatted = date.toLocaleString('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
  return `${formatted} ${easternTzAbbr(date)}`
}

export function formatTime (dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''

  const formatted = date.toLocaleTimeString('en-US', {
    timeZone: EST_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
  return `${formatted} ${easternTzAbbr(date)}`
}

export function formatDateForFilename (dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })

  const parts = formatter.formatToParts(date)
  const year = parts.find(p => p.type === 'year').value
  const month = parts.find(p => p.type === 'month').value
  const day = parts.find(p => p.type === 'day').value

  return `${year}-${month}-${day}`
}

export function getESTDate (dateStr) {
  if (!dateStr) return null
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return null

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  const parts = formatter.formatToParts(date)
  const year = parts.find(p => p.type === 'year').value
  const month = parts.find(p => p.type === 'month').value
  const day = parts.find(p => p.type === 'day').value
  const hour = parts.find(p => p.type === 'hour').value
  const minute = parts.find(p => p.type === 'minute').value
  const second = parts.find(p => p.type === 'second').value

  return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`)
}

export function getESTNow () {
  const now = new Date()
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  const parts = formatter.formatToParts(now)
  const year = parts.find(p => p.type === 'year').value
  const month = parts.find(p => p.type === 'month').value
  const day = parts.find(p => p.type === 'day').value
  const hour = parts.find(p => p.type === 'hour').value
  const minute = parts.find(p => p.type === 'minute').value
  const second = parts.find(p => p.type === 'second').value

  return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`)
}

export function getESTToday () {
  const today = getESTNow()
  today.setHours(0, 0, 0, 0)
  return today
}

// Normalise the non-standard "YYYY-MM-DD HH:MM AM/PM EST|EDT" format produced by
// the server's est_date_format serialiser into a proper ISO 8601 UTC string.
// The trailing abbreviation is informational only — the correct UTC offset is
// resolved by probing America/New_York below, so this handles EST and EDT alike.
// Safari/WebKit rejects the original format in new Date(); V8/Chrome is lenient.
export function normalizeApiDate (dateStr) {
  if (!dateStr) return dateStr
  const m = dateStr.match(
    /^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2})\s+(AM|PM)\s+(?:EST|EDT)$/
  )
  if (!m) return dateStr // already in a parseable format

  const [, datePart, rawH, min, ampm] = m
  let h = parseInt(rawH, 10)
  if (ampm === 'PM' && h !== 12) h += 12
  if (ampm === 'AM' && h === 12) h = 0

  const [year, month, day] = datePart.split('-').map(Number)

  // Probe UTC+4 (EDT) and UTC+5 (EST) to find the correct offset for this
  // specific date in America/New_York (handles DST automatically).
  const hourFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TIMEZONE,
    hour: '2-digit',
    hour12: false
  })

  for (const offset of [4, 5]) {
    const candidate = new Date(Date.UTC(year, month - 1, day, h + offset, parseInt(min, 10), 0))
    const parts = hourFormatter.formatToParts(candidate)
    const candidateHour = parseInt(parts.find(p => p.type === 'hour').value) % 24
    if (candidateHour === h) {
      return candidate.toISOString()
    }
  }

  // Fallback: assume EST (UTC-5)
  return new Date(Date.UTC(year, month - 1, day, h + 5, parseInt(min, 10), 0)).toISOString()
}

export function convertToESTDate (dateStr) {
  if (!dateStr) return null

  // Handle non-standard "YYYY-MM-DD HH:MM AM/PM EST" format from the API
  const apiFormatMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})\s+\d{1,2}:\d{2}\s+(?:AM|PM)\s+(?:EST|EDT)$/)
  if (apiFormatMatch) {
    return convertToESTDate(normalizeApiDate(dateStr))
  }

  const dateOnlyMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch
    const yearNum = parseInt(year)
    const monthNum = parseInt(month) - 1
    const dayNum = parseInt(day)

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: EST_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })

    for (let utcHour = 4; utcHour <= 6; utcHour++) {
      const testDate = new Date(
        Date.UTC(yearNum, monthNum, dayNum, utcHour, 0, 0, 0)
      )
      const testParts = timeFormatter.formatToParts(testDate)
      const testYear = testParts.find(p => p.type === 'year').value
      const testMonth = testParts.find(p => p.type === 'month').value
      const testDay = testParts.find(p => p.type === 'day').value
      const testHour = testParts.find(p => p.type === 'hour').value

      if (
        testYear === year &&
        testMonth === month &&
        testDay === day &&
        testHour === '00'
      ) {
        return testDate
      }
    }

    return new Date(Date.UTC(yearNum, monthNum, dayNum, 5, 0, 0, 0))
  }

  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return null

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  const parts = dateFormatter.formatToParts(date)
  const year = parts.find(p => p.type === 'year').value
  const month = parts.find(p => p.type === 'month').value
  const day = parts.find(p => p.type === 'day').value

  for (let utcHour = 4; utcHour <= 6; utcHour++) {
    const testDate = new Date(
      Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        utcHour,
        0,
        0,
        0
      )
    )

    const testParts = timeFormatter.formatToParts(testDate)
    const testYear = testParts.find(p => p.type === 'year').value
    const testMonth = testParts.find(p => p.type === 'month').value
    const testDay = testParts.find(p => p.type === 'day').value
    const testHour = testParts.find(p => p.type === 'hour').value

    if (
      testYear === year &&
      testMonth === month &&
      testDay === day &&
      testHour === '00'
    ) {
      return testDate
    }
  }

  return new Date(
    Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 5, 0, 0, 0)
  )
}

// Format date with custom options and EST suffix
export function formatDateEST (dateStr, options = {}) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''

  const formatted = date.toLocaleDateString('en-US', {
    timeZone: EST_TIMEZONE,
    ...options
  })
  return `${formatted} ${easternTzAbbr(date)}`
}
